import React, { useState, useMemo } from 'react';
import type { Category } from '@util/insights/topSearchesNov2025';

export type SortOption =
  | 'price_desc'
  | 'brand'
  | 'model';

export type RankScope = 'top10' | 'top25' | 'top50';

interface ControlBarProps {
  brands: string[];
  categories: Category[];
  onFilterChange: (filters: FilterState) => void;
}

export interface FilterState {
  search: string;
  selectedCategories: Category[];
  selectedBrands: string[];
  rankScope: RankScope;
  priceRange: [number, number];
  sort: SortOption;
}

export const ControlBar: React.FC<ControlBarProps> = ({
  brands,
  categories,
  onFilterChange,
}) => {
  const [search, setSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [rankScope, setRankScope] = useState<RankScope>('top50');
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [sort, setSort] = useState<SortOption>('brand');

  // Debounced search
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const handleSearchChange = (value: string) => {
    setSearch(value);

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      emitFilterChange({ search: value });
    }, 250);

    setSearchDebounceTimer(timer);
  };

  const emitFilterChange = (overrides?: Partial<FilterState>) => {
    onFilterChange({
      search,
      selectedCategories,
      selectedBrands,
      rankScope,
      priceRange,
      sort,
      ...overrides,
    });
  };

  const handleCategoryToggle = (category: Category) => {
    const newCategories = selectedCategories.includes(category)
      ? selectedCategories.filter((c) => c !== category)
      : [...selectedCategories, category];
    setSelectedCategories(newCategories);
    emitFilterChange({ selectedCategories: newCategories });
  };

  const handleBrandToggle = (brand: string) => {
    const newBrands = selectedBrands.includes(brand)
      ? selectedBrands.filter((b) => b !== brand)
      : [...selectedBrands, brand];
    setSelectedBrands(newBrands);
    emitFilterChange({ selectedBrands: newBrands });
  };

  const handleRankScopeChange = (scope: RankScope) => {
    setRankScope(scope);
    emitFilterChange({ rankScope: scope });
  };

  const handleSortChange = (newSort: SortOption) => {
    setSort(newSort);
    emitFilterChange({ sort: newSort });
  };

  const getCategoryLabel = (category: Category): string => {
    const labels: Record<Category, string> = {
      Camera: 'Cameras',
      Lens: 'Lenses',
      Drone: 'Drones',
      Accessory: 'Accessories',
      Lighting: 'Lighting',
      Video: 'Video',
    };
    return labels[category];
  };

  const handleClearFilters = () => {
    setSearch('');
    setSelectedCategories([]);
    setSelectedBrands([]);
    setRankScope('top50');
    setPriceRange([0, 10000]);
    setSort('brand');
    onFilterChange({
      search: '',
      selectedCategories: [],
      selectedBrands: [],
      rankScope: 'top50',
      priceRange: [0, 10000],
      sort: 'brand',
    });
  };

  const hasActiveFilters =
    search ||
    selectedCategories.length > 0 ||
    selectedBrands.length > 0 ||
    rankScope !== 'top50';

  return (
    <div className="bg-gray-200 border border-gray-200 rounded-lg p-4 mb-6 space-y-4">
      {/* Row 1: Search + Category Filter  */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Search */}
        <div>
          <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <input
            id="search"
            type="text"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Search brand or model..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          />
        </div>

        {/* Category Filter (Segmented Control) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                setSelectedCategories([]);
                emitFilterChange({ selectedCategories: [] });
              }}
              className={`px-4 py-2 text-2xl rounded-md transition-colors ${
                selectedCategories.length === 0
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => handleCategoryToggle(category)}
                className={`px-4 py-2 text-2xl rounded-md transition-colors ${
                  selectedCategories.includes(category)
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {getCategoryLabel(category)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 2: Brand Filter + Rank Scope */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Brand Filter (Multi-select) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Brands {selectedBrands.length > 0 && `(${selectedBrands.length})`}
          </label>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {brands.map((brand) => (
              <button
                key={brand}
                onClick={() => handleBrandToggle(brand)}
                className={`px-3 py-2 text-2xl rounded transition-colors ${
                  selectedBrands.includes(brand)
                    ? 'bg-brand-orange text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {brand}
              </button>
            ))}
          </div>
        </div>

        {/* Rank Scope */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Show
          </label>
          <div className="flex gap-2">
            {(['top10', 'top25', 'top50'] as RankScope[]).map((scope) => (
              <button
                key={scope}
                onClick={() => handleRankScopeChange(scope)}
                className={`px-6 py-3 text-2xl rounded-md transition-colors ${
                  rankScope === scope
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {scope === 'top10' ? 'Top 10' : scope === 'top25' ? 'Top 25' : 'Top 50'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Sort + Clear Filters */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Sort */}
        <div className="flex items-center gap-2">
          <label htmlFor="sort" className="text-sm font-medium text-gray-700">
            Sort by:
          </label>
          <select
            id="sort"
            value={sort}
            onChange={(e) => handleSortChange(e.target.value as SortOption)}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-orange focus:border-transparent"
          >
            <option value="price_desc">Median price (desc)</option>
            <option value="brand">Brand (A→Z)</option>
            <option value="model">Model (A→Z)</option>
          </select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="text-sm text-gray-600 hover:text-gray-900 underline"
          >
            Clear all filters
          </button>
        )}
      </div>
    </div>
  );
};
